import clsx from 'clsx'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import { rem } from 'polished'
import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import zIndex from 'styles/tools/z-index'
import useHasMounted from 'hooks/useHasMounted'
import { useElementHeight } from 'hooks/useElementSize'
import TripDrawerHeader from './TripDrawerHeader'

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOutToLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`

const backdropAppear = keyframes`
  from {
    background-color: rgba(0, 0, 0, 0);
    backdrop-filter: blur(0px);
  }
  to {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
`

const backdropDisappear = keyframes`
  from {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
  to {
    background-color: rgba(0, 0, 0, 0);
    backdrop-filter: blur(0px);
  }
`

const TRIP_DRAWER_MAX_WIDTH_PX = 960

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
  animation-name: ${backdropAppear};

  &.closing {
    animation-name: ${backdropDisappear};
  }
`

const Container = styled.div<{ $maxWidth: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 75%;
  max-width: ${({ $maxWidth }) => rem($maxWidth)};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: 2;
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;

  &.direction-left-to-right {
    inset-inline-start: 0;
    animation-name: ${slideInFromLeft};
    box-shadow: ${rem(4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);

    &.closing {
      animation-name: ${slideOutToLeft};
    }

    [dir="rtl"] & {
      animation-name: ${slideInFromRight};
      box-shadow: ${rem(-4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);
    }

    [dir="rtl"] &.closing {
      animation-name: ${slideOutToRight};
    }
  }

  &.direction-right-to-left {
    inset-inline-end: 0;
    animation-name: ${slideInFromRight};
    box-shadow: ${rem(-4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);

    &.closing {
      animation-name: ${slideOutToRight};
    }

    [dir="rtl"] & {
      animation-name: ${slideInFromLeft};
      box-shadow: ${rem(4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);
    }

    [dir="rtl"] &.closing {
      animation-name: ${slideOutToLeft};
    }
  }

  &.with-overlay {
    box-shadow: none;
  }
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  &.has-footer {
    padding-bottom: var(--drawer-footer-height, 0px);
  }
`

const FloatingFooter = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  z-index: 10;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

interface Props {
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  direction: 'left-to-right' | 'right-to-left'
  showOverlay?: boolean
  children: ReactNode
  header?: ReactNode
  footer?: ReactNode
  onBack?: () => void
  headerActions?: ReactNode
  onDelete?: () => void
  maxWidth?: number
}

function TripDrawer({
  isOpen,
  onClose,
  onAfterClose,
  direction,
  showOverlay = true,
  children,
  header,
  footer,
  onBack,
  headerActions,
  onDelete,
  maxWidth = TRIP_DRAWER_MAX_WIDTH_PX,
}: Props) {
  const hasMounted = useHasMounted()
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isClosing, setIsClosing] = useState(false)
  const footerRef = useRef<HTMLDivElement>(null)
  const footerHeight = useElementHeight(footerRef)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
        onAfterClose?.()
      }, MODAL_TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isOpen, shouldRender, onAfterClose])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.defaultPrevented) return
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    if (shouldRender) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shouldRender, handleKeyDown])

  const handleOverlayClick = useCallback(() => {
    onClose()
  }, [onClose])

  if (!shouldRender || !hasMounted || typeof document === 'undefined') {
    return null
  }

  const isRtl = document.documentElement.dir === 'rtl'
  const closeOnLeft = direction === 'right-to-left' ? !isRtl : isRtl

  return createPortal(
    <Root>
      {showOverlay && (
        <Overlay
          className={clsx({ closing: isClosing })}
          onClick={handleOverlayClick}
        />
      )}
      <Container
        $maxWidth={maxWidth}
        className={clsx(
          `direction-${direction}`,
          { closing: isClosing },
          { 'with-overlay': showOverlay },
        )}
      >
        <TripDrawerHeader
          closeOnLeft={closeOnLeft}
          onClose={onClose}
          onBack={onBack}
          onDelete={onDelete}
          headerActions={headerActions}
        >
          {header}
        </TripDrawerHeader>
        <Body
          className={clsx({ 'has-footer': Boolean(footer) })}
          style={{ '--drawer-footer-height': footerHeight ? rem(footerHeight) : undefined } as CSSProperties}
        >
          {children}
        </Body>
        {footer && <FloatingFooter ref={footerRef}>{footer}</FloatingFooter>}
      </Container>
    </Root>,
    document.body,
  )
}

export default TripDrawer
